/**
 * Image Gallery Component
 * Displays patient's X-rays and photos (basic version)
 */

import { Image as ImageIcon, Calendar } from 'lucide-react';
import type { DentalOfficeImage } from '../../types';
import { formatDate } from '../../lib/utils';
import { LoadingSpinner } from '../LoadingSpinner';

interface ImageGalleryProps {
  images: DentalOfficeImage[] | undefined;
  isLoading: boolean;
}

export function ImageGallery({ images, isLoading }: ImageGalleryProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner text="Hleð myndum..." />
      </div>
    );
  }

  if (!images || images.length === 0) {
    return (
      <div className="text-center py-12">
        <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Engar myndir skráðar</p>
        <p className="text-sm text-gray-500 mt-2">
          Röntgenmyndir og ljósmyndir munu birtast hér
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {images.map((image) => (
        <div
          key={image.id}
          className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-brand-primary transition-colors"
        >
          {/* Image Placeholder */}
          <div className="aspect-video bg-gray-100 flex items-center justify-center">
            <ImageIcon className="w-12 h-12 text-gray-400" />
            <p className="ml-2 text-sm text-gray-500">
              {image.image_type.startsWith('xray') ? 'Röntgenmynd' : 'Ljósmynd'}
            </p>
          </div>

          {/* Image Details */}
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-semibold text-gray-900 text-sm">
                {image.description || 'Ónefnd mynd'}
              </h4>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  image.image_type.startsWith('xray')
                    ? 'bg-info/10 text-info'
                    : image.image_type.startsWith('photo')
                    ? 'bg-success/10 text-success'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {image.image_type.startsWith('xray')
                  ? 'Röntgen'
                  : image.image_type.startsWith('photo')
                  ? 'Mynd'
                  : 'Annað'}
              </span>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(image.created_at)}</span>
            </div>

            {image.tooth_number && (
              <p className="mt-2 text-xs text-gray-600">Tönn: {image.tooth_number}</p>
            )}

            {image.description && (
              <p className="mt-2 text-xs text-gray-600 bg-gray-50 rounded p-2">
                {image.description}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
